'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

export interface TopBannerItem {
  imageUrlDesktop: string;
  imageUrlMobile: string;
  href: string;
  alt?: string;
  duration: number;
}

export interface TopBannerProps {
  banners: TopBannerItem[];
}

/** Loading shell. Mobile stays a 40px bar; desktop follows the 1440×48 artwork ratio. */
export const TOP_BANNER_FRAME_CLASS =
  'relative block h-10 w-full md:aspect-[1440/48] md:h-auto';

export function TopBanner({ banners }: TopBannerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const validBanners = banners.filter(
    ({ imageUrlDesktop, imageUrlMobile, href, duration }) =>
      (imageUrlDesktop || imageUrlMobile) && href && duration > 0
  );

  useEffect(() => {
    if (validBanners.length <= 1) {
      return;
    }

    const currentBanner = validBanners[currentIndex];

    const timeout = window.setTimeout(() => {
      setCurrentIndex((index) => (index + 1) % validBanners.length);
    }, currentBanner.duration);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [currentIndex, validBanners]);

  if (validBanners.length === 0) {
    return null;
  }

  const banner = validBanners[currentIndex];
  const bannerAlt = banner.alt?.trim() || 'بنر تبلیغاتی فروشگاه ویترو';

  const isExternalLink =
    banner.href.startsWith('http://') || banner.href.startsWith('https://');

  return (
    <Link
      key={`${currentIndex}-${banner.imageUrlMobile}-${banner.imageUrlDesktop}`}
      href={banner.href}
      {...(isExternalLink
        ? {
            target: '_blank',
            rel: 'noopener noreferrer',
          }
        : {})}
      className="relative block h-10 w-full overflow-hidden md:h-auto md:overflow-visible"
    >
      <Image
        src={banner.imageUrlMobile || banner.imageUrlDesktop}
        alt={bannerAlt}
        fill
        sizes="100vw"
        priority={currentIndex === 0}
        className="object-cover md:hidden"
      />
      <Image
        src={banner.imageUrlDesktop || banner.imageUrlMobile}
        alt={bannerAlt}
        width={1440}
        height={48}
        sizes="100vw"
        priority={currentIndex === 0}
        className="hidden h-auto w-full object-contain md:block"
      />
    </Link>
  );
}
